import React, {
    useContext,
    useCallback,
    isValidElement,
    cloneElement,
    ReactElement,
    Children,
} from 'react';
import { cn } from '../../utils';
import {
    ChevronRightIcon,
    FolderIcon,
    DocumentIcon,
} from '@heroicons/react/24/outline';
import { TreeContext, TreeNodeProps, IconWrapperProps } from './TreeContext';

function IconWrapper({ icon, disabled }: IconWrapperProps) {
    return (
        <div
            data-testid="icon"
            className={cn(
                'alli-flex alli-h-5 alli-w-5 alli-justify-center alli-pl-1',
                {
                    'alli-text-gray-500': disabled,
                    'alli-text-blue-gray-700': !disabled,
                },
            )}>
            {icon}
        </div>
    );
}

function TreeNode({
    id,
    disabled,
    children,
    depth = 0,
    prefix,
    suffix,
    nodeClassName,
}: TreeNodeProps) {
    const { expandedNodes, activeItemIds, toggleNode, defaultIcon, navIcon } =
        useContext(TreeContext);
    const isExpanded = expandedNodes.includes(id);
    const isActive = activeItemIds.includes(id);
    const paddingLeft = `${depth > 0 ? depth * 28 - (depth - 1) * 8 : 8}px`;

    const [childNodes, label] = React.useMemo(() => {
        const childArray = React.Children.toArray(children);
        const nodeChildren = childArray.filter(
            child => React.isValidElement(child) && child.type === TreeNode,
        );
        const nodeLabel = childArray.find(
            child =>
                typeof child === 'string' ||
                typeof child === 'number' ||
                React.isValidElement(child),
        );
        return [nodeChildren, nodeLabel];
    }, [children]);

    const renderIcon = useCallback(() => {
        if (defaultIcon) {
            return (
                <IconWrapper
                    icon={
                        childNodes.length > 0 ? (
                            <FolderIcon />
                        ) : (
                            <DocumentIcon />
                        )
                    }
                    disabled={disabled || false}
                />
            );
        }
        return null;
    }, [defaultIcon, children, disabled]);

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            if (disabled) return;

            if (childNodes.length > 0) {
                toggleNode(id);
            }
            if (React.isValidElement(label) && label.props.onClick) {
                label.props.onClick(e);
            }
        },
        [disabled, id, toggleNode, childNodes.length, label],
    );

    return (
        <div>
            <div
                data-testid={`tree-node-${id}`}
                className={cn(
                    'alli-flex alli-cursor-pointer alli-px-2 alli-py-3 alli-transition-all alli-duration-150 alli-ease-in-out hover:alli-bg-blue-gray-100',
                    { 'alli-text-gray-500': disabled },
                    { 'alli-bg-blue-gray-100': isActive },
                    nodeClassName,
                )}
                onClick={handleClick}
                style={{ paddingLeft }}>
                {childNodes.length > 0 && navIcon && (
                    <div className="alli-flex alli-h-5 alli-w-5 alli-rotate-0 alli-transform alli-text-gray-700 alli-transition-transform alli-duration-150 alli-ease-in-out">
                        <ChevronRightIcon
                            className={cn(
                                { 'alli-rotate-90': isExpanded },
                                { 'alli-text-gray-500': disabled },
                            )}
                        />
                    </div>
                )}
                {prefix ? prefix : renderIcon()}
                <div className="alli-overflow-hidden alli-text-ellipsis alli-whitespace-nowrap alli-text-nowrap alli-pl-2">
                    {label}
                </div>
                {suffix}
            </div>
            {isExpanded && childNodes.length > 0 && (
                <div>
                    {Children.map(childNodes, child => {
                        if (isValidElement(child)) {
                            return cloneElement(
                                child as ReactElement<TreeNodeProps>,
                                {
                                    depth: depth + 1,
                                },
                            );
                        }
                        return child;
                    })}
                </div>
            )}
        </div>
    );
}

export default TreeNode;
